// TS twin of iOS `AppClipInvocation`.
//
// Mirrors the Swift parser at autoviz-ato/Models/AppClipInvocation.swift
// EXCEPT this implementation also enforces the `usdz=` host allow-list
// flagged as a MEDIUM finding in the iOS code-quality audit (audit §7).
//
// When the iOS audit fix lands, both implementations will agree on every
// row of `fixtures/url-invocations.json`. Until then, the iOS
// invariant test will fail on the "evil.io" row — that failure IS the
// regression pin reminding us to land the iOS host allow-list.

import { assemblyCode, type AssemblyCode } from '@/models/assembly-code'
import {
  appEnvironment,
  APP_ENVIRONMENT_DEFAULT,
  type AppEnvironment,
} from '@/models/app-environment'

/// Hosts permitted for the legacy `?usdz=` direct download path.
/// Anything else is treated as a malicious / spoofed invocation and rejected.
const USDZ_ALLOWED_HOSTS = new Set<string>([
  'assembler.autoviz.io',
  'staging.assembler.autoviz.io',
])

export interface AppClipInvocation {
  readonly code: AssemblyCode | null
  readonly modelURL: URL | null
  readonly wheelId: string | null
  readonly returnURL: URL | null
  readonly diameter: number | null
  readonly customizationParams: string | null
  readonly env: AppEnvironment
  readonly clientHostURL: string | null
  readonly usesPollingFlow: boolean
}

export function parseInvocation(url: URL): AppClipInvocation | null {
  const params = url.searchParams

  const code = assemblyCode(params.get('code'))
  const urlParam = params.get('usdz') ?? params.get('url')

  if (code == null && urlParam == null) {
    return null
  }

  // Legacy direct-URL path: parse + validate host allow-list.
  let modelURL: URL | null = null
  if (urlParam != null) {
    const parsedModelURL = safeParseURL(urlParam)
    if (parsedModelURL == null) {
      // Malformed URL string — fail closed.
      return code != null ? buildInvocation(url, code, null, params) : null
    }
    if (!USDZ_ALLOWED_HOSTS.has(parsedModelURL.host)) {
      // Disallowed host — fail closed.
      return code != null ? buildInvocation(url, code, null, params) : null
    }
    modelURL = parsedModelURL
  }

  return buildInvocation(url, code, modelURL, params)
}

function buildInvocation(
  url: URL,
  code: AssemblyCode | null,
  modelURL: URL | null,
  params: URLSearchParams,
): AppClipInvocation {
  const pathSegments = url.pathname.split('/').filter((s) => s.length > 0)
  // Path format: /ar/{wheelId}
  let wheelId: string | null = null
  if (pathSegments.length >= 2 && pathSegments[0] === 'ar') {
    wheelId = pathSegments[1]
  } else {
    wheelId = params.get('id')
  }

  const returnParam = params.get('return')
  const returnURL = returnParam != null ? safeParseURL(returnParam) : null

  const diameterParam = params.get('diameter')
  const diameter =
    diameterParam != null && Number.isFinite(Number(diameterParam))
      ? Number(diameterParam)
      : null

  const customizationParams = params.get('cp')
  const env = appEnvironment(params.get('env'))
  const clientHostURL = params.get('client_host_url')

  return {
    code,
    modelURL,
    wheelId,
    returnURL,
    diameter,
    customizationParams,
    env: env ?? APP_ENVIRONMENT_DEFAULT,
    clientHostURL,
    usesPollingFlow: code != null,
  }
}

function safeParseURL(raw: string): URL | null {
  try {
    return new URL(raw)
  } catch {
    return null
  }
}
